import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDebounceCallback } from 'usehooks-ts'

import styles from '../VolunteeringFiltersModal.module.scss'
import { FilterProps } from '../VolunteeringFiltersModal.types'

import { AsyncSelect, Box, Chip, SelectOption, Typography } from '@percent/lemonade'
import { OpportunityLocationTypeDto } from '@percent/workplace-giving/api/opportunity/dto'
import { getCityFacetValues } from '@percent/workplace-giving/api/search/facetValues/facetValues'
import { useQuery } from '@percent/workplace-giving/common/hooks'

const MIN_CITY_SEARCH_LENGTH = 2

export function CityFilter({ selectedFilters, setSelectedFilters }: FilterProps) {
  const { t } = useTranslation()
  const [query, setQuery] = useState('')
  const debouncedSetQuery = useDebounceCallback(setQuery, 200)

  const isVirtual = selectedFilters.locationType === OpportunityLocationTypeDto.Virtual
  const selectedCities = selectedFilters.city ?? []

  useEffect(() => {
    if (isVirtual) {
      setSelectedFilters(prev => (prev.city?.length ? { ...prev, city: undefined } : prev))
    }
  }, [isVirtual, setSelectedFilters])

  const { data: facetValues, isFetching } = useQuery(['cityFacetValues', query], getCityFacetValues, {
    enabled: !isVirtual && query.length >= MIN_CITY_SEARCH_LENGTH,
    staleTime: 1000 * 60
  })

  const options: SelectOption[] = (facetValues ?? [])
    .map(({ value }) => value)
    .filter(value => !selectedCities.includes(value))
    .map(value => ({ value, label: value }))

  const addCity = (option: SelectOption | null) => {
    if (!option || selectedCities.includes(option.value)) {
      return
    }
    setSelectedFilters({ ...selectedFilters, city: [...selectedCities, option.value] })
    setQuery('')
  }

  const removeCity = (value: string) => {
    setSelectedFilters({ ...selectedFilters, city: selectedCities.filter(selected => selected !== value) })
  }

  return (
    <Box>
      <Typography className={styles.FiltersSectionTitle}>{t('workplace_giving.volunteering.filters.city')}</Typography>
      <AsyncSelect
        placeholder={t('workplace_giving.volunteering.filters.cityPlaceholder') as string}
        disabled={isVirtual}
        loading={isFetching}
        options={options}
        query={query}
        setQuery={debouncedSetQuery}
        onChange={addCity}
        minSearchLength={MIN_CITY_SEARCH_LENGTH}
        clearQueryOnSelect
        clearable
        data-testid="cityFilterSelect"
      />
      {selectedCities.length > 0 ? (
        <Box style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', marginTop: '0.5rem' }}>
          {selectedCities.map(city => (
            <Chip
              key={city}
              value={city}
              icon="close"
              onClick={() => removeCity(city)}
              testId={`cityFilterChip-${city}`}
            />
          ))}
        </Box>
      ) : null}
    </Box>
  )
}
